/**
 * Company (HireWithUs) credits API – same backend as Admin CreditManagement.
 * Uses API_BASE_URL and same /api/credits endpoints with current company id.
 */
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';

const creditsApi = axios.create({
  baseURL: `${API_BASE_URL}/api/credits`,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// This is a custom axios instance, so the global interceptor does NOT cover it.
// Attach the signed tokens so the (now-gated) credit routes authorize this
// company cross-domain, where the session cookie is dropped.
creditsApi.interceptors.request.use((config) => {
  try {
    const et = sessionStorage.getItem('employer_token') || localStorage.getItem('employer_token');
    const at = sessionStorage.getItem('admin_token') || localStorage.getItem('admin_token');
    config.headers = config.headers || {};
    if (et && !config.headers['X-Employer-Token']) config.headers['X-Employer-Token'] = et;
    if (at && !config.headers['X-Admin-Token']) config.headers['X-Admin-Token'] = at;
  } catch (e) {
    /* never break the request */
  }
  return config;
});

function getCompanyId() {
  if (typeof window === 'undefined') return null;
  return (
    localStorage.getItem('companyAccountId') ||
    localStorage.getItem('companyId') ||
    localStorage.getItem('companyID') ||
    sessionStorage.getItem('companyAccountId') ||
    sessionStorage.getItem('companyId') ||
    null
  );
}

/**
 * GET /companies/:id – same as admin CompanyManageModal fetchCompanyDetails.
 * Returns company details (credit_balance, plan_id, plan_name, etc.).
 */
export function getCompanyDetails(companyId) {
  const id = companyId || getCompanyId();
  if (!id) return Promise.reject(new Error('No company id'));
  return creditsApi.get(`/companies/${id}`);
}

/**
 * GET /transactions?company_id= – same as admin CreditUsagePage.
 */
export function getTransactions(companyId, params = {}) {
  const id = companyId || getCompanyId();
  if (!id) return Promise.reject(new Error('No company id'));
  return creditsApi.get('/transactions', { params: { company_id: id, ...params } });
}

/**
 * GET /plans – same as admin.
 */
export function getPlans() {
  return creditsApi.get('/plans');
}

/**
 * POST /company/:id/change-plan – same as admin CompanyManageModal savePlan.
 */
export function changePlan(companyId, planId) {
  const id = companyId || getCompanyId();
  if (!id) return Promise.reject(new Error('No company id'));
  return creditsApi.post(`/company/${id}/change-plan`, { plan_id: planId });
}

export const companyCreditsApi = {
  getCompanyId,
  getSummary: getCompanyDetails,
  getTransactions,
  getPlans,
  changePlan(payload) {
    const id = getCompanyId();
    return changePlan(id, payload?.plan_id ?? payload);
  },
};

export default companyCreditsApi;
